import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { LogOut, Check, Clock, Circle, FileText, Building2, Plane, Award, MapPin } from "lucide-react";
import Logo from "./Logo";

const STAGE_ICONS = {
    review: FileText,
    apply: Building2,
    loa: Award,
    visa: MapPin,
    depart: Plane,
};

export default function Dashboard() {
    const navigate = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState(location.state?.user || null);
    const [progress, setProgress] = useState(null);
    const [loading, setLoading] = useState(!location.state?.user);

    useEffect(() => {
        const load = async () => {
            try {
                if (!user) {
                    const u = await api.get("/auth/me");
                    setUser(u.data);
                }
                const p = await api.get("/portal/progress");
                setProgress(p.data);
            } catch (e) {
                navigate("/login");
            } finally {
                setLoading(false);
            }
        };
        load();
        // eslint-disable-next-line
    }, []);

    const logout = async () => {
        await api.post("/auth/logout");
        navigate("/");
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
                <div className="text-sm text-slate-500">Memuat dashboard, Kak...</div>
            </div>
        );
    }

    const stages = progress?.stages || [];
    const currentIdx = progress?.current_stage ?? 0;
    const completed = stages.filter((s) => s.status === "completed").length;
    const pct = stages.length ? Math.round(((completed + 0.5) / stages.length) * 100) : 0;

    return (
        <div className="min-h-screen bg-[#F8FAFC]" data-testid="dashboard-page">
            <header className="bg-white border-b border-slate-100">
                <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
                    <Link to="/" className="flex-shrink-0">
                        <Logo size={40} />
                    </Link>
                    <div className="flex items-center gap-4">
                        {user?.picture && <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />}
                        <div className="hidden sm:block text-right">
                            <div className="text-sm font-semibold text-[#0A1A2F]" data-testid="dash-user-name">{user?.name}</div>
                            <div className="text-xs text-slate-500" data-testid="dash-user-email">{user?.email}</div>
                        </div>
                        <button onClick={logout} data-testid="logout-btn" className="text-slate-500 hover:text-[#0A1A2F] p-2" title="Logout">
                            <LogOut className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 md:px-12 py-10">
                <div className="mb-8">
                    <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#0A1A2F]">
                        Halo, Kak {user?.name?.split(" ")[0] || ""}! 👋
                    </h1>
                    <p className="text-slate-600 mt-2">Berikut perkembangan aplikasi kuliahmu hari ini.</p>
                </div>

                {/* Progress overview */}
                <div className="bg-gradient-to-br from-[#0A1A2F] to-[#11294A] rounded-3xl p-8 text-white mb-8 relative overflow-hidden">
                    <div className="absolute -top-20 -right-20 w-60 h-60 bg-[#F5A623]/20 rounded-full blur-3xl" />
                    <div className="relative">
                        <div className="text-xs uppercase tracking-[0.2em] text-[#F5A623] font-bold mb-2">Status Aplikasi</div>
                        <div className="font-display text-3xl sm:text-4xl font-bold">
                            Tahap: {stages[currentIdx]?.label || "—"}
                        </div>
                        <div className="mt-6">
                            <div className="flex justify-between text-xs text-slate-300 mb-2">
                                <span>Progress keseluruhan</span>
                                <span data-testid="progress-percent">{pct}%</span>
                            </div>
                            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${pct}%` }}
                                    transition={{ duration: 1 }}
                                    className="h-full bg-[#F5A623] rounded-full"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stages */}
                <div className="grid md:grid-cols-5 gap-4" data-testid="stages-grid">
                    {stages.map((s, i) => {
                        const Icon = STAGE_ICONS[s.key] || Circle;
                        const isActive = i === currentIdx;
                        const isDone = s.status === "completed" || i < currentIdx;
                        const getIconBoxClass = () => {
                            if (isDone) return "bg-green-100 text-green-600";
                            if (isActive) return "bg-[#F5A623]/15 text-[#F5A623]";
                            return "bg-slate-100 text-slate-400";
                        };
                        const renderIcon = () => {
                            if (isDone) return <Check className="w-5 h-5" />;
                            if (isActive) return <Clock className="w-5 h-5" />;
                            return <Icon className="w-5 h-5" />;
                        };
                        const getStatusClass = () => {
                            if (isDone) return "text-green-600";
                            if (isActive) return "text-[#F5A623]";
                            return "text-slate-400";
                        };
                        const getStatusText = () => {
                            if (isDone) return "Selesai";
                            if (isActive) return "Berlangsung";
                            return "Menunggu";
                        };
                        return (
                            <motion.div
                                key={s.key}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.08 }}
                                className={`relative rounded-2xl border p-5 ${
                                    isActive ? "bg-white border-[#F5A623] shadow-lg shadow-[#F5A623]/10" : "bg-white border-slate-200"
                                }`}
                                data-testid={`stage-card-${s.key}`}
                            >
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${getIconBoxClass()}`}>
                                    {renderIcon()}
                                </div>
                                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Tahap {i + 1}</div>
                                <div className="font-display font-semibold text-[#0A1A2F] mt-1">{s.label}</div>
                                <div className={`mt-2 text-xs font-medium ${getStatusClass()}`}>
                                    {getStatusText()}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                <div className="mt-10 grid md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-2xl border border-slate-200 p-6">
                        <h3 className="font-display font-semibold text-lg text-[#0A1A2F]">Konsultan Penanggung Jawab</h3>
                        <div className="mt-4 flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-[#F5A623]/20 flex items-center justify-center font-bold text-[#F5A623]">G</div>
                            <div>
                                <div className="font-semibold text-[#0A1A2F]">Tim Gantari Senior</div>
                                <div className="text-sm text-slate-500">Akan menghubungi Kakak via WhatsApp dalam 1x24 jam.</div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-2xl border border-slate-200 p-6">
                        <h3 className="font-display font-semibold text-lg text-[#0A1A2F]">Catatan dari Tim</h3>
                        <p className="mt-3 text-sm text-slate-600">Selamat datang Kak! Mohon siapkan: transkrip nilai, scan paspor, dan sertifikat bahasa. Upload via Chat GARI atau email ke hello@gantariedu.com ya.</p>
                    </div>
                </div>
            </main>
        </div>
    );
}
